import { useEffect, useState, useMemo } from 'react'
import { useTable } from 'react-table'
import { useTranslation } from 'react-i18next'

import API from '../../../api/api'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'

import { getActionsStatuses } from './constants'
import { getLocationId } from './helpers'

export const useFetchLocations = ({ buildingId }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState([])

  const fetchData = async (buildingId) => {
    if (!buildingId) {
      return
    }
    setData(null)
    setError(null)
    setIsLoading(true)

    try {
      const response = await API.getDigitalSignageLocation(buildingId)
      setData(response.data)
    } catch (error) {
      setError(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData(buildingId)
  }, [buildingId])

  return { isLoading, error, data, refetch: fetchData }
}

export const useDigitalSignageTable = ({ locations, setTvApp }) => {
  const { t } = useTranslation(['digital-signage', 'common'])
  const ACTIONS_STATUSES = getActionsStatuses(t)
  const { displaySnackbar } = useSnackbar()
  const locationsData =
    locations && locations.length > 0
      ? locations.map(({ shortUrl, editUrl, locationName }) => ({
          appLink: shortUrl,
          editUrl,
          location: locationName,
        }))
      : []

  const columns = useMemo(
    () => [
      {
        Header: t('digital-signage:table.locationHeader'),
        accessor: 'location',
      },
      {
        Header: t('digital-signage:table.appLinkHeader'),
        accessor: 'appLink',
        Cell: (data) => {
          const { value } = data

          const copyToClipboard = async (text) => {
            displaySnackbar(ACTIONS_STATUSES.TV_APP_LINK_COPIED)
            if (navigator.clipboard) {
              try {
                await navigator.clipboard.writeText(text)
              } catch (err) {
                console.error(err)
              }
            }
          }

          return (
            <div className="tv-app-link-row">
              <div className="tv-app-link">{value}</div>
              <div className="copy-icon" onClick={() => copyToClipboard(value)}>
                <img
                  className="img-icon"
                  src="/images/digitalSignage/copy.svg"
                  alt={t('digital-signage:table.copyIconAlt')}
                />
              </div>
            </div>
          )
        },
      },
      {
        Header: t('digital-signage:table.actionsHeader'),
        accessor: 'actions',
        Cell: (data) => {
          const { editUrl, location } = data.cell.row.original
          const locationId = getLocationId(editUrl)

          const onEdit = (e) => {
            e.preventDefault()
            window.open(editUrl, '_blank')
          }

          const onDeleteLocation = () => {
            setTvApp({ id: locationId, locationName: location })
          }

          return (
            <div className="actions">
              <button onClick={onEdit}>{t('common:actions.edit')}</button>
              <button onClick={onDeleteLocation}>{t('common:actions.delete')}</button>
            </div>
          )
        },
      },
    ],
    [t, displaySnackbar, ACTIONS_STATUSES, setTvApp],
  )

  return useTable({
    columns,
    data: locationsData,
  })
}
